/* ============================================================
   replies.jsx — the keyword-matched canned responses from js/aiassistant.js.

   These were HTML strings assembled with innerHTML. They are JSX now, which is
   the whole point: the prototype's chat was also the one place anyone had
   thought about escaping, and they did it partially (`text.replace(/</g,'&lt;')`
   on the input only — not &, " or '). JSX escapes everything, everywhere.

   Still a stub. Wire a real endpoint into Chat's `send` to make it live.
   ============================================================ */

export function reply(prompt, { candidates, recruiters }) {
  const p = prompt.toLowerCase()

  if (p.includes('rank')) {
    const top = [...candidates].sort((a, b) => b.aiScore - a.aiScore).slice(0, 5)
    return (
      <>
        <p>Here are the top-ranked candidates by ATS match score:</p>
        <ul>
          {top.map((c, i) => (
            <li key={c.id}>
              <b>{i + 1}. {c.name}</b> — {c.aiScore}% match · {c.jobTitle} · {c.recommendation}
            </li>
          ))}
        </ul>
        <p className="text-muted">
          This is a UI preview. Connect an LLM endpoint to generate live rankings from
          resume + JD embeddings.
        </p>
      </>
    )
  }

  if (p.includes('compare')) {
    const [a, b] = candidates.slice(0, 2)
    return (
      <>
        <p>Comparing <b>{a.name}</b> vs <b>{b.name}</b>:</p>
        <ul>
          <li><b>Experience:</b> {a.experience}y vs {b.experience}y</li>
          <li><b>ATS Score:</b> {a.aiScore}% vs {b.aiScore}%</li>
          <li><b>Recommendation:</b> {a.recommendation} vs {b.recommendation}</li>
        </ul>
        <p><b>Suggested:</b> {a.aiScore >= b.aiScore ? a.name : b.name} appears stronger on core criteria.</p>
      </>
    )
  }

  if (p.includes('job description') || p.includes('jd')) {
    return (
      <>
        <p><b>Senior Product Designer</b></p>
        <p>
          We’re looking for a Senior Product Designer to craft intuitive, delightful
          experiences across our platform. You’ll own end-to-end design, from research to
          polished UI, and partner closely with product and engineering.
        </p>
        <p><b>Responsibilities:</b> lead design for key initiatives, run user research, build and maintain design systems, mentor peers.</p>
        <p><b>Requirements:</b> 5+ years product design, strong portfolio, fluency in Figma, systems thinking.</p>
      </>
    )
  }

  if (p.includes('interview question')) {
    return (
      <>
        <p>Here are role-specific interview questions:</p>
        <ul>
          <li>Walk me through how you’d design a system to handle 1M concurrent users.</li>
          <li>Describe a technically challenging project and the tradeoffs you made.</li>
          <li>How do you approach debugging a production incident under time pressure?</li>
          <li>Tell me about a time you disagreed with a teammate on an approach.</li>
        </ul>
      </>
    )
  }

  if (p.includes('summar')) {
    const c = candidates[0]
    return (
      <>
        <p><b>Resume summary — {c.name}</b></p>
        <p>
          {c.experience} years of experience, currently {c.currentTitle} at {c.currentCompany}.
          Strong in {c.skills.slice(0, 3).join(', ')}. ATS match {c.aiScore}% for {c.jobTitle}. {c.recommendation}.
        </p>
      </>
    )
  }

  if (p.includes('email')) {
    return (
      <>
        <p><b>Subject:</b> Interview Invitation — Next Steps</p>
        <p>Hi [Candidate],</p>
        <p>
          Thank you for applying. We were impressed by your background and would love to
          invite you to an interview. Please share your availability for this week.
        </p>
        <p>Best regards,<br />Talent Team</p>
      </>
    )
  }

  if (p.includes('offer letter')) {
    return (
      <>
        <p><b>Offer Letter</b></p>
        <p>
          Dear [Candidate], We are pleased to offer you the position of Product Manager at a
          base salary of $160,000, plus equity and benefits. This offer is contingent on
          standard background checks.
        </p>
        <p>We’re excited about the possibility of you joining the team.</p>
      </>
    )
  }

  if (p.includes('skill gap')) {
    return (
      <>
        <p><b>Skill Gap Analysis — Engineering pipeline</b></p>
        <ul>
          <li><span className="skill-pill skill-missing">Kubernetes</span> under-represented (only 22% of pipeline)</li>
          <li><span className="skill-pill skill-missing">System Design</span> gap at senior level</li>
          <li><span className="skill-pill skill-matched">React</span> well covered</li>
        </ul>
        <p>Consider sourcing candidates with cloud-native infra experience.</p>
      </>
    )
  }

  if (p.includes('pipeline')) {
    return (
      <>
        <p><b>Pipeline health analysis</b></p>
        <ul>
          <li>{candidates.length} active candidates across 6 stages</li>
          <li>Conversion Shortlist → Interview: ~28%</li>
          <li>Bottleneck detected at <b>Assessment</b> stage (longest dwell time)</li>
          <li>Offer acceptance trending at 82%</li>
        </ul>
        <p>Recommendation: accelerate assessment turnaround to improve velocity.</p>
      </>
    )
  }

  if (p.includes('productivity') || p.includes('recruiter')) {
    const top = [...recruiters].sort((a, b) => b.hires - a.hires)[0]
    return (
      <>
        <p><b>Team productivity this month</b></p>
        <ul>
          <li>Top performer: {top?.name}</li>
          <li>Avg time-to-hire: 27 days (3 days faster than last month)</li>
          <li>Interview completion rate: 91%</li>
        </ul>
      </>
    )
  }

  if (p.includes('recommend') || p.includes('suggest')) {
    const c = [...candidates].sort((a, b) => b.aiScore - a.aiScore)[0]
    return (
      <p>
        <b>Top recommendation:</b> {c.name} ({c.aiScore}% match) for {c.jobTitle}. Strong on{' '}
        {c.matchedSkills.slice(0, 2).join(' & ')}. I’d prioritise scheduling a screen this week.
      </p>
    )
  }

  return (
    <>
      <p>
        I can help with ranking candidates, comparing profiles, drafting JDs, interview
        questions, emails, offer letters, skill-gap and pipeline analysis, and more.
      </p>
      <p className="text-muted">
        This is a fully-designed interface. Wire an AI endpoint (Claude / OpenAI) into the
        chat’s <code>send</code> handler to make responses live.
      </p>
    </>
  )
}
